// consultantCopy — an answer as plain text, for the clipboard.
//
// ⚠️ IT COPIES WHAT THE READER SAW, INCLUDING THE SOURCES. The consultant's
// value is that every figure is cited; an answer pasted into an email with the
// citations stripped is exactly the artefact this product exists not to
// produce. Tables come across as readable rows and the provenance line comes
// with them, so a number that leaves the app still says where it came from.
//
// ⚠️ NOT A MARKDOWN EXPORT. This is for pasting into a text message or an
// email, so it stays plain: no pipes, no backticks, nothing that renders as
// noise in a mail client. The PDF and report builder already cover the case
// where someone wants a formatted document.

import type { MessagePart } from "@/types/consultant";
import type { Provenance } from "@/types/consultantProvenance";

/** One "Source: …" line per distinct citation, in the order they appeared. */
function sourceLines(parts: MessagePart[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of parts) {
    const prov = (part as { provenance?: Provenance | Provenance[] }).provenance;
    if (!prov) continue;
    for (const p of Array.isArray(prov) ? prov : [prov]) {
      // ⚠️ report_name, not the slug. The registry name is what the model
      // cites on screen ("LM_CT100 5-Area Daily Direct Slaughter Cattle"), and
      // a pasted citation that reads "2466" is not a citation to anyone.
      const label = [p.report_name, p.as_of_date, p.usda_url]
        .filter((v): v is string => typeof v === "string" && v.trim() !== "")
        .join(" · ");
      if (label && !seen.has(label)) {
        seen.add(label);
        out.push(label);
      }
    }
  }
  return out;
}

/**
 * Flatten an assistant answer into pasteable text.
 *
 * Returns an empty string when there is nothing a reader would recognise as
 * the answer — the caller should not offer a copy button in that case.
 */
export function answerToText(parts: MessagePart[]): string {
  const blocks: string[] = [];

  for (const part of parts) {
    switch (part.type) {
      case "text":
        if (part.content?.trim()) blocks.push(part.content.trim());
        break;

      case "table": {
        // ⚠️ Tabs, not pipes. A pipe table is markdown that only renders
        // somewhere that parses markdown; tabs paste into a mail client, a
        // notes app and a spreadsheet alike.
        const head = part.columns?.join("\t");
        const rows = (part.rows ?? []).map((r) =>
          Array.isArray(r) ? r.map((c) => String(c ?? "")).join("\t") : "",
        );
        const title = part.title?.trim();
        blocks.push([title, head, ...rows].filter(Boolean).join("\n"));
        break;
      }

      case "chart":
        // A chart cannot be pasted. Its title carries what it showed.
        if (part.title?.trim()) blocks.push(`[chart] ${part.title.trim()}`);
        break;

      case "link":
        blocks.push(`${part.label}: ${part.href}`);
        break;

      // Follow-up chips are navigation, not part of the answer.
      case "suggestions":
        break;
    }
  }

  const body = blocks.filter(Boolean).join("\n\n").trim();
  if (!body) return "";

  const sources = sourceLines(parts);
  return sources.length
    ? `${body}\n\nSources:\n${sources.map((s) => `- ${s}`).join("\n")}`
    : body;
}
